import React, { useState } from "react";
import LegalLayout from "./LegalLayout";
import { api, formatApiError } from "../../lib/api";
import { CheckCircle2, Loader2, ArrowLeft } from "lucide-react";

const emptyForm = { full_name: "", email: "", order_number: "", products: "", reason: "" };

const RecessoOnline = () => {
  const [step, setStep] = useState("form"); // form | review | done
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const isValid = form.full_name.trim().length >= 2 && /\S+@\S+\.\S+/.test(form.email) &&
    form.order_number.trim().length > 0 && form.products.trim().length > 0;

  const goToReview = (e) => {
    e.preventDefault();
    if (!isValid) { setError("Compila tutti i campi obbligatori (motivo escluso) con dati validi."); return; }
    setError("");
    setStep("review");
  };

  const confirmWithdrawal = async () => {
    setSubmitting(true);
    setError("");
    try {
      await api.post("/withdrawal-requests", form);
      setStep("done");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const fieldCls = "mt-2 w-full bg-[#0A1121] border border-white/10 rounded-sm px-4 py-3 text-white text-sm focus:outline-none focus:border-[#C0C0C0] placeholder:text-[#64748B]";
  const labelCls = "text-[10px] tracking-[0.28em] uppercase text-[#94A3B8]";

  return (
    <LegalLayout testid="page-recesso-online" eyebrow="Documento legale" title="Recedi dal contratto qui" updated="Ottobre 2026">
      <p>
        Puoi esercitare il diritto di recesso entro <strong>14 giorni</strong> dalla ricezione dei
        prodotti compilando questo modulo online: riceverai subito un'email di ricevuta con i
        dettagli della tua richiesta. In alternativa puoi usare il{" "}
        <a href="/modulo-recesso">modulo PDF scaricabile</a>.
      </p>

      {step === "form" && (
        <form onSubmit={goToReview} className="not-prose space-y-5 mt-6" data-testid="recesso-form">
          <label className="block">
            <span className={labelCls}>Nome e cognome *</span>
            <input value={form.full_name} onChange={set("full_name")} required data-testid="recesso-input-name" className={fieldCls} placeholder="Mario Rossi" />
          </label>
          <label className="block">
            <span className={labelCls}>Email *</span>
            <input type="email" value={form.email} onChange={set("email")} required data-testid="recesso-input-email" className={fieldCls} placeholder="mario.rossi@email.it" />
          </label>
          <label className="block">
            <span className={labelCls}>Numero d'ordine *</span>
            <input value={form.order_number} onChange={set("order_number")} required data-testid="recesso-input-order-number" className={fieldCls} placeholder="Es. LEO-10234" />
          </label>
          <label className="block">
            <span className={labelCls}>Prodotti da restituire *</span>
            <textarea value={form.products} onChange={set("products")} required rows={3} data-testid="recesso-input-products" className={fieldCls} placeholder="Es. 1x Xamin Tabs 200 cpr" />
          </label>
          <label className="block">
            <span className={labelCls}>Motivo (facoltativo)</span>
            <textarea value={form.reason} onChange={set("reason")} rows={2} data-testid="recesso-input-reason" className={fieldCls} placeholder="Facoltativo" />
          </label>

          {error && (
            <p className="text-[12px] text-red-300 bg-red-500/10 border border-red-500/30 rounded-sm px-3 py-2" data-testid="recesso-error">
              {error}
            </p>
          )}

          <button
            type="submit"
            data-testid="recesso-continue-btn"
            className="inline-flex items-center gap-2 bg-[#C0C0C0] text-[#060A14] px-6 py-3.5 rounded-sm text-[11px] tracking-[0.28em] uppercase hover:bg-white transition-colors"
          >
            Continua
          </button>
        </form>
      )}

      {step === "review" && (
        <div className="not-prose mt-6 space-y-5" data-testid="recesso-review">
          <h2 className="font-[Outfit] text-white text-lg">Riepilogo della richiesta</h2>
          <div className="bg-[#0A1121] border border-white/10 rounded-md p-5 space-y-3 text-[13px]">
            {[
              ["Nome e cognome", form.full_name],
              ["Email", form.email],
              ["Numero d'ordine", form.order_number],
              ["Prodotti da restituire", form.products],
              ["Motivo", form.reason || "—"],
            ].map(([label, value]) => (
              <div key={label} className="flex items-start justify-between gap-4">
                <span className="text-[#94A3B8] shrink-0">{label}</span>
                <span className="text-[#E5E4E2] text-right">{value}</span>
              </div>
            ))}
          </div>

          {error && (
            <p className="text-[12px] text-red-300 bg-red-500/10 border border-red-500/30 rounded-sm px-3 py-2" data-testid="recesso-error">
              {error}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => setStep("form")}
              data-testid="recesso-edit-btn"
              className="inline-flex items-center gap-2 border border-white/15 text-[#94A3B8] px-5 py-3 rounded-sm text-[11px] tracking-[0.2em] uppercase hover:text-white hover:border-white/30"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Modifica
            </button>
            <button
              type="button"
              onClick={confirmWithdrawal}
              disabled={submitting}
              data-testid="recesso-confirm-btn"
              className="inline-flex items-center gap-2 bg-[#C0C0C0] text-[#060A14] px-6 py-3.5 rounded-sm text-[11px] tracking-[0.28em] uppercase hover:bg-white transition-colors disabled:opacity-50"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Conferma recesso
            </button>
          </div>
        </div>
      )}

      {step === "done" && (
        <div className="not-prose mt-6 bg-emerald-500/10 border border-emerald-500/30 rounded-md p-6" data-testid="recesso-success">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="w-6 h-6 text-emerald-300 shrink-0 mt-0.5" />
            <div>
              <h2 className="font-[Outfit] text-white text-lg">Richiesta ricevuta</h2>
              <p className="mt-2 text-[13px] text-[#CBD5E1]">
                Ti abbiamo inviato un'email di ricevuta a <strong>{form.email}</strong> con i dettagli
                della richiesta e data e ora di ricezione. Ti risponderemo a breve con le istruzioni
                per la restituzione e il rimborso.
              </p>
            </div>
          </div>
        </div>
      )}
    </LegalLayout>
  );
};

export default RecessoOnline;
